import Button from 'react-bootstrap/Button';
import { useFormikContext } from 'formik';
import { MOCK_STORES } from '../core/_mock';
import { type TicketDetailsValues, type TicketFormField, type TicketSla } from '../core/_models';
import { dueWeekdayLabel, slaBusinessHoursLabel } from '../core/_utils';
import TicketFormFieldInput from './TicketFormFieldInput';
import TicketSelectField from './TicketSelectField';
import TicketTextField from './TicketTextField';

const ATTACHMENT_NAME = 'arquivo-anexo.jpg';

type Props = {
  destination: string;
  sla: TicketSla;
  dueAt?: string;
  form: TicketFormField[];
  descriptionHelp?: string;
  conversationLabel?: string;
  instruction?: string;
  relatedTicketNumber?: number;
};

const TicketDetailsStep = ({
  destination,
  sla,
  dueAt,
  form,
  descriptionHelp,
  conversationLabel,
  instruction,
  relatedTicketNumber,
}: Props) => {
  const { values, setFieldValue } = useFormikContext<TicketDetailsValues>();
  const attachmentsLabel = values.attachments.join(', ');
  const hoursLabel = slaBusinessHoursLabel(sla);
  const dueLabel = dueAt ? dueWeekdayLabel(dueAt) : '';
  const deadlineSuffix = dueLabel ? ` · até ${dueLabel}` : '';
  const hasForm = form.length > 0;

  const addAttachment = () => setFieldValue('attachments', [...values.attachments, ATTACHMENT_NAME]);

  return (
    <div>
      <div className="d-flex align-items-start gap-3 rounded bg-gray-100 px-4 py-3 mb-6">
        <i className="ki-duotone ki-message-question fs-2 text-gray-700 mt-1">
          <span className="path1" />
          <span className="path2" />
          <span className="path3" />
        </i>
        <div className="d-flex flex-column gap-1 flex-grow-1 min-w-0">
          <span className="text-gray-900 fw-bold fs-6">{destination}</span>
          <span className="d-flex flex-wrap align-items-center gap-1 text-gray-700 fs-7">
            <i className="ki-duotone ki-timer fs-5 text-gray-600">
              <span className="path1" />
              <span className="path2" />
              <span className="path3" />
            </i>{' '}
            Resposta em até <b className="text-gray-900">{hoursLabel}</b>
            {deadlineSuffix}
          </span>
        </div>
        <span className="d-none d-md-inline text-gray-600 fs-8 fw-semibold text-nowrap">Etapa 2 de 2</span>
      </div>
      {relatedTicketNumber ? (
        <div className="badge badge-light-primary mb-5">Relacionado ao #{relatedTicketNumber}</div>
      ) : null}
      <TicketSelectField
        required
        help="Você atende mais de uma loja. Escolha a que precisa do atendimento."
        label="Loja"
        name="store"
        options={MOCK_STORES}
        placeholder="Selecione a loja"
      />
      {instruction ? (
        <p className="text-gray-700 fs-7 mb-5">
          <b className="text-gray-900">Antes de abrir:</b> {instruction}
        </p>
      ) : null}
      {form.map((field, index) => (
        <TicketFormFieldInput key={field.label} field={field} index={index} />
      ))}
      <TicketTextField
        multiline
        help={descriptionHelp}
        label="Descrição"
        name="description"
        placeholder="Conte o que está acontecendo"
        required={!hasForm}
      />
      <div className="mb-5">
        <div className="form-label fw-bold fs-7 text-gray-700">Anexos</div>
        {conversationLabel ? (
          <div className="text-gray-700 fs-7 mb-3">
            <b className="text-gray-900">Vai junto:</b> {conversationLabel}
          </div>
        ) : null}
        <Button size="sm" variant="light" onClick={addAttachment}>
          Anexar arquivo
        </Button>
        {values.attachments.length > 0 ? <div className="text-gray-700 fs-7 mt-3">{attachmentsLabel}</div> : null}
      </div>
    </div>
  );
};

export default TicketDetailsStep;
